import { BSC_ABI } from '@lfg/abis';
import { useBoolean } from '@lfg/core/hooks';
import { AxiosError } from 'axios';
import { useCallback } from 'react';
import { toast } from 'react-toastify';
import { waitForTransactionReceipt, writeContract } from 'wagmi/actions';

import { apiClientV1 } from '@/agent/apiClient/api';
import TransactionSubmittedToast from '@/components/TransactionSubmittedToast';
import { NODE_SALE_V1_SC_ADDRESS } from '@/global/configs/common.config';
import { ETH_DECIMAL, USD_VALUE_DECIMAL } from '@/global/configs/decimal.config';
import type { GetWalletSessionResponseSchemaType, WalletSessionType } from '@/global/schemas/claimGala.schema';
import { useWeb3 } from '@/hooks';
import { useClaimGalaV1Mutation, useWalletConnectV1Mutation } from '@/store/services/claimGala.service';
import { useAppSelector } from '@/store/utils';
import { logError } from '@/utils/helpers';

import type { CheckNowProps, ClaimGalaProps, ClaimGalaV1Hook, VerifyProps } from '../types/claimGala.type';
import { usePriceV1 } from './usePriceV1';

const useClaimGalaV1 = (): ClaimGalaV1Hook => {
  const { config, address } = useWeb3();
  const { getPriceInUSD } = usePriceV1();
  const { nodeId: nft } = useAppSelector(state => state.app);
  const { project } = useAppSelector(state => state.project);
  const [claimGalaV1] = useClaimGalaV1Mutation();
  const [walletConnectV1] = useWalletConnectV1Mutation();
  const { value: isClaiming, setTrue: setIsClaimingTrue, setFalse: setIsClaimingFalse } = useBoolean(false);
  const { value: isChecking, setTrue: setIsCheckingTrue, setFalse: setIsCheckingFalse } = useBoolean(false);
  const { value: isVerifying, setTrue: setIsVerifyingTrue, setFalse: setIsVerifyingFalse } = useBoolean(false);

  const handleVerify = useCallback(
    async ({ txHashId }: VerifyProps): Promise<WalletSessionType | undefined> => {
      setIsVerifyingTrue();
      try {
        const respsone = await apiClientV1.post<GetWalletSessionResponseSchemaType>(
          `/v1/wallets/${address}/sessions/verify`,
          {
            txHashId,
          },
        );
        toast.success('Verification successful');
        return respsone.data.data;
      } catch (error) {
        if (error instanceof AxiosError) {
          toast.error(error.response?.data?.message || 'Error verifying');
        } else {
          toast.error('Error verifying');
          throw error;
        }
      } finally {
        setIsVerifyingFalse();
      }
    },
    [address, setIsVerifyingFalse, setIsVerifyingTrue],
  );

  const handleCheckNow = useCallback(
    async (data: CheckNowProps): Promise<WalletSessionType | undefined> => {
      setIsCheckingTrue();
      try {
        const response = await walletConnectV1(data);
        if (response.data?.isError) {
          toast.error(response.data.errorMessage || 'Check GalaChain Address failed');
          return;
        }

        toast.success('Check now successfully');
        return response.data;
      } catch (error: any) {
        toast.error('Check now failed');
        throw error;
      } finally {
        setIsCheckingFalse();
      }
    },
    [walletConnectV1, setIsCheckingFalse, setIsCheckingTrue],
  );

  const handleClaim = useCallback(
    async ({ availableNode, onSuccess }: ClaimGalaProps) => {
      if (!address) return;
      setIsClaimingTrue();
      try {
        const claimRes = await claimGalaV1({ wAddress: address });
        if (!claimRes.data) {
          toast.error('Claim referral failed');
          return;
        }

        const currentPriceInUsd = await getPriceInUSD({
          amount: availableNode,
          isLoadingAgain: false,
          currentTierPrice: project?.currentTier?.price,
        });

        if (!currentPriceInUsd) {
          toast.error('Claim redeem failed');
          return;
        }

        const {
          price,
          decimals,
          deadline,
          nonce,
          signature: { r, s, v },
        } = claimRes.data;

        const hash = await writeContract(config, {
          abi: BSC_ABI,
          address: NODE_SALE_V1_SC_ADDRESS,
          functionName: `buyByETH`,
          args: [
            Math.ceil(currentPriceInUsd * 1.008 * 10 ** USD_VALUE_DECIMAL),
            {
              nft,
              amount: availableNode,
              buyer: address,
              price,
              decimals,
              deadline,
              v,
              r,
              signature: s,
              nonce,
            },
          ],
          // HACK: Define min value of ETH that allows user to mint - can discuss with SmartContract
          // developer for more information
          // I just tested, and find that it will be failed if the value is less than 8000
          value: BigInt(
            Math.max(10000, Math.ceil((currentPriceInUsd * 10 ** (ETH_DECIMAL + decimals) * 1.03) / price)),
          ),
        });

        toast(TransactionSubmittedToast());
        const receipt = await waitForTransactionReceipt(config, { hash });
        toast.success('Claimed redeem successfully');
        onSuccess?.(receipt.transactionHash);
      } catch (error: unknown) {
        const errorDetails = logError(error, 'handleClaimGala');
        const errorMessage = errorDetails.message || (error instanceof Error ? error.message : 'Claim redeem failed');
        toast.error(errorMessage);
        throw error;
      } finally {
        setIsClaimingFalse();
      }
    },
    [address, project, config, nft, claimGalaV1, getPriceInUSD, setIsClaimingFalse, setIsClaimingTrue],
  );

  return {
    isClaiming,
    isChecking,
    isVerifying,
    handleClaim,
    handleCheckNow,
    handleVerify,
  };
};

export { useClaimGalaV1 };
